import { useEffect, useRef, useState } from "react";
import { api, apiUpload } from "@/lib/api";
import { toast } from "sonner";
import { MapPin, Camera, Save, Plus } from "lucide-react";

const inputCls = "w-full rounded-md border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-cfhtb-light focus:border-cfhtb-secondary";
const labelCls = "block text-sm font-medium text-cfhtb-heading mb-1.5";

const STATUTS = {
  BROUILLON: ["Brouillon", "bg-slate-100 text-slate-600 border-slate-200"],
  EN_ATTENTE: ["En attente de validation", "bg-cfhtb-accent/15 text-cfhtb-accent border-cfhtb-accent/30"],
  VALIDE: ["Validée — visible dans l'annuaire", "bg-cfhtb-success/10 text-cfhtb-success border-cfhtb-success/30"],
  REFUSE: ["Refusée — contactez la CFHTB", "bg-cfhtb-error/10 text-cfhtb-error border-cfhtb-error/30"],
};

const EMPTY = {
  cabinet_nom: "", adresse: "", code_postal: "", ville: "", pays: "France",
  specialites: "", publics: "", langues: "", telephone_public: "",
  email_public: "", site_web: "", presentation: "",
  teleconsultation: false, visible_sur_carte: true,
};

const toList = (s) => s.split(",").map((x) => x.trim()).filter(Boolean);

export default function MaFicheAnnuaire() {
  const [fiche, setFiche] = useState(undefined);
  const [form, setForm] = useState(null);
  const [busy, setBusy] = useState(false);
  const [photoBusy, setPhotoBusy] = useState(false);
  const fileRef = useRef(null);

  const load = () => api("/membre/fiche").then(setFiche).catch((e) => toast.error(e.message));
  useEffect(() => { load(); }, []);

  const openForm = () => {
    if (fiche) {
      setForm({
        ...EMPTY,
        ...Object.fromEntries(Object.keys(EMPTY).map((k) => [k, fiche[k] ?? EMPTY[k]])),
        specialites: (fiche.specialites || []).join(", "),
        publics: (fiche.publics || []).join(", "),
        langues: (fiche.langues || []).join(", "),
        visible_sur_carte: fiche.visible_sur_carte !== false,
      });
    } else {
      setForm({ ...EMPTY });
    }
  };

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const save = async (e) => {
    e.preventDefault();
    setBusy(true);
    const body = {
      ...form,
      specialites: toList(form.specialites), publics: toList(form.publics),
      langues: toList(form.langues),
      cabinet_nom: form.cabinet_nom || null, adresse: form.adresse || null,
      code_postal: form.code_postal || null, ville: form.ville || null,
      telephone_public: form.telephone_public || null, email_public: form.email_public || null,
      site_web: form.site_web || null, presentation: form.presentation || null,
    };
    try {
      await api("/membre/fiche", { method: "PUT", body });
      toast.success("Fiche enregistrée — elle sera visible dans l'annuaire après validation par la CFHTB.");
      setForm(null);
      load();
    } catch (err) {
      toast.error(err.message);
    } finally {
      setBusy(false);
    }
  };

  const uploadPhoto = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setPhotoBusy(true);
    const fd = new FormData();
    fd.append("file", file);
    try {
      await apiUpload("/membre/fiche/photo", fd);
      toast.success("Photo enregistrée (visible après validation de la fiche)");
      load();
    } catch (err) {
      toast.error(err.message);
    } finally {
      setPhotoBusy(false);
      e.target.value = "";
    }
  };

  if (fiche === undefined) return null;

  const badge = fiche && STATUTS[fiche.statut_validation];

  return (
    <section className="bg-white rounded-lg border border-cfhtb-light/30 shadow-sm p-6 space-y-5" data-testid="ma-fiche-annuaire">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <MapPin className="h-5 w-5 text-cfhtb-secondary" />
          <div>
            <h2 className="font-serif text-xl font-semibold text-cfhtb-heading">Ma fiche annuaire</h2>
            <p className="text-xs text-cfhtb-text">Informations professionnelles publiées sur la carte des praticiens.</p>
          </div>
        </div>
        {badge && (
          <span className={`text-xs font-medium px-2.5 py-1 rounded-full border ${badge[1]}`} data-testid="fiche-statut-badge">
            {badge[0]}
          </span>
        )}
      </div>

      {!form ? (
        <div className="flex flex-wrap items-center gap-4">
          {fiche?.photo_url && (
            <img src={fiche.photo_url} alt="Ma photo" data-testid="fiche-photo-preview"
              className="h-16 w-16 rounded-full object-cover border border-cfhtb-light/30" />
          )}
          <div className="flex-1 min-w-[200px] text-sm text-cfhtb-text">
            {fiche ? (
              <p>
                {[fiche.cabinet_nom, fiche.adresse, `${fiche.code_postal || ""} ${fiche.ville || ""}`.trim()]
                  .filter(Boolean).join(" · ") || "Fiche sans adresse"}
              </p>
            ) : (
              <p>Vous n'avez pas encore de fiche annuaire.</p>
            )}
          </div>
          <div className="flex gap-2">
            {fiche && (
              <>
                <input ref={fileRef} type="file" accept=".jpg,.jpeg,.png,.webp" className="hidden"
                  data-testid="fiche-photo-input" onChange={uploadPhoto} />
                <button type="button" onClick={() => fileRef.current?.click()} disabled={photoBusy}
                  data-testid="fiche-photo-button"
                  className="inline-flex items-center gap-2 bg-white border border-slate-300 text-cfhtb-text rounded-md px-4 py-2 text-sm font-medium hover:bg-slate-50 transition-colors disabled:opacity-60">
                  <Camera className="h-4 w-4" /> {photoBusy ? "Envoi…" : "Photo"}
                </button>
              </>
            )}
            <button type="button" onClick={openForm} data-testid="fiche-edit-button"
              className="inline-flex items-center gap-2 bg-cfhtb-primary text-white rounded-md px-4 py-2 text-sm font-medium hover:bg-[#1f1975] transition-colors">
              {fiche ? <><Save className="h-4 w-4" /> Modifier ma fiche</> : <><Plus className="h-4 w-4" /> Créer ma fiche</>}
            </button>
          </div>
        </div>
      ) : (
        <form onSubmit={save} className="space-y-4" data-testid="fiche-form">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className={labelCls}>Nom du cabinet / structure</label>
              <input className={inputCls} data-testid="fiche-cabinet" value={form.cabinet_nom} onChange={(e) => set("cabinet_nom", e.target.value)} />
            </div>
            <div>
              <label className={labelCls}>Téléphone public</label>
              <input className={inputCls} data-testid="fiche-telephone" value={form.telephone_public} onChange={(e) => set("telephone_public", e.target.value)} />
            </div>
          </div>
          <div>
            <label className={labelCls}>Adresse</label>
            <input className={inputCls} data-testid="fiche-adresse" value={form.adresse} onChange={(e) => set("adresse", e.target.value)} placeholder="N° et rue" />
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
            <div>
              <label className={labelCls}>Code postal</label>
              <input className={inputCls} data-testid="fiche-cp" value={form.code_postal} onChange={(e) => set("code_postal", e.target.value)} />
            </div>
            <div>
              <label className={labelCls}>Ville</label>
              <input className={inputCls} data-testid="fiche-ville" value={form.ville} onChange={(e) => set("ville", e.target.value)} />
            </div>
            <div className="col-span-2 sm:col-span-1">
              <label className={labelCls}>Pays</label>
              <input className={inputCls} data-testid="fiche-pays" value={form.pays} onChange={(e) => set("pays", e.target.value)} />
            </div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <div>
              <label className={labelCls}>Spécialités (séparées par des virgules)</label>
              <input className={inputCls} data-testid="fiche-specialites" value={form.specialites} onChange={(e) => set("specialites", e.target.value)} placeholder="hypnose medicale, hypnoanalgesie" />
            </div>
            <div>
              <label className={labelCls}>Publics</label>
              <input className={inputCls} data-testid="fiche-publics" value={form.publics} onChange={(e) => set("publics", e.target.value)} placeholder="adultes, adolescents" />
            </div>
            <div>
              <label className={labelCls}>Langues</label>
              <input className={inputCls} data-testid="fiche-langues" value={form.langues} onChange={(e) => set("langues", e.target.value)} placeholder="français, anglais" />
            </div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className={labelCls}>Email public</label>
              <input className={inputCls} data-testid="fiche-email" value={form.email_public} onChange={(e) => set("email_public", e.target.value)} />
            </div>
            <div>
              <label className={labelCls}>Site web</label>
              <input className={inputCls} data-testid="fiche-site" value={form.site_web} onChange={(e) => set("site_web", e.target.value)} placeholder="https://…" />
            </div>
          </div>
          <div>
            <label className={labelCls}>Présentation</label>
            <textarea rows={3} className={inputCls} data-testid="fiche-presentation" value={form.presentation} onChange={(e) => set("presentation", e.target.value)} />
          </div>
          <div className="flex flex-wrap gap-6">
            <label className="flex items-center gap-2 text-sm text-cfhtb-heading cursor-pointer">
              <input type="checkbox" data-testid="fiche-teleconsultation" checked={form.teleconsultation}
                onChange={(e) => set("teleconsultation", e.target.checked)}
                className="h-4 w-4 rounded border-slate-300 text-cfhtb-primary focus:ring-cfhtb-light" />
              Je propose des téléconsultations
            </label>
            <label className="flex items-center gap-2 text-sm text-cfhtb-heading cursor-pointer">
              <input type="checkbox" data-testid="fiche-visible" checked={form.visible_sur_carte}
                onChange={(e) => set("visible_sur_carte", e.target.checked)}
                className="h-4 w-4 rounded border-slate-300 text-cfhtb-primary focus:ring-cfhtb-light" />
              Visible sur la carte de l'annuaire
            </label>
          </div>
          <p className="text-xs text-cfhtb-text/80 bg-cfhtb-bg/60 rounded-md px-3 py-2">
            Après enregistrement, votre fiche repasse en validation : elle sera publiée (ou ses modifications appliquées) après approbation par la CFHTB.
          </p>
          <div className="flex justify-end gap-3">
            <button type="button" onClick={() => setForm(null)}
              className="bg-white text-cfhtb-text border border-slate-300 rounded-md px-4 py-2 text-sm font-medium hover:bg-slate-50">
              Annuler
            </button>
            <button type="submit" disabled={busy} data-testid="fiche-save-button"
              className="inline-flex items-center gap-2 bg-cfhtb-primary text-white rounded-md px-5 py-2.5 text-sm font-medium hover:bg-[#1f1975] transition-colors disabled:opacity-60">
              <Save className="h-4 w-4" /> {busy ? "Enregistrement…" : "Enregistrer ma fiche"}
            </button>
          </div>
        </form>
      )}
    </section>
  );
}
